<!--
 * @Description: 提现弹框。服务商可输入提现金额。供应商不可输入，通过订单选择 
 * @Date: 2025-05-20 15:57:04
-->

<template>
  <BaseDialog
    title="提现到账银行卡"
    class="form-dialog"
    width="450px"
    :model-value="true"
    :show-close="true"
    :close-on-press-escape="false"
    :close-on-click-modal="false"
    :showFooter="false"
  >
    <BaseTextContent style="max-height: 500px; overflow-y: scroll">
      <BaseForm
        class="enterprise-real-form-content"
        ref="formRef"
        label-position="top"
        :model="formData"
        :rules="rules"
      >
        <BaseFormItem label="公司名称" prop="companyName">
          <BaseInput
            class="form-dialog__w280"
            type="text"
            disabled
            v-model="withdrawalInfo.companyName"
          ></BaseInput>
        </BaseFormItem>
        <BaseFormItem label="开户行" prop="bankName">
          <BaseInput
            class="form-dialog__w280"
            type="text"
            disabled
            v-model="withdrawalInfo.bankName"
          ></BaseInput>
        </BaseFormItem>
        <BaseFormItem label="开户名" prop="openName">
          <BaseInput
            class="form-dialog__w280"
            type="text"
            disabled
            v-model="withdrawalInfo.openName"
          ></BaseInput>
        </BaseFormItem>
        <BaseFormItem label="银行卡号" prop="openBankNumber">
          <BaseInput
            class="form-dialog__w280"
            type="text"
            disabled
            v-model="withdrawalInfo.openBankNumber"
          ></BaseInput>
        </BaseFormItem>

        <BaseFormItem label="提现金额" prop="amount">
          <!--  
            :min="+formatter.fen2yuan(minAmount)"
            :max="+formatter.fen2yuan(withdrawalInfo.amount)"
           -->
          <BaseInputNumber
            style="width: 100%"
            v-if="showAmountInput"
            valueOnClear="max"
            clearable
            :placeholder="`请输入可提现金额 ${formatter.fen2yuan(minAmount)}`"
            :disabled="amountDisabled"
            :controls="false"
            :precision="2"
            :min="0"
            :max="translator.fen2yuan(withdrawalInfo.amount)"
            v-model="formData.amount"
          />
        </BaseFormItem>
        <div style="color: red">
          <div v-if="isAspWithdrawal">
            当前可提现金额：{{ formatter.fen2yuan(withdrawalInfo.amount) }}元
          </div>
          <div>
            手续费：{{ formatter.fen2yuan(withdrawalInfo.withdrawFee) }}元
          </div>
          <div v-if="formData.amount">
            实际到账金额：{{ formatter.fen2yuan(amountReceived) }}元
          </div>
        </div>
        <BaseFormItem prop="remark">
          <BaseInput
            type="textarea"
            style="width: 100%; margin-top: 20px"
            placeholder="请输入备注，最大支持100字符"
            clearable
            show-word-limit
            :rows="3"
            :maxlength="100"
            v-model="formData.remark"
          />
        </BaseFormItem>
        <BaseFormItem
          prop="verifyCode"
          :label="`提现验证 ${withdrawalInfo.phone}`"
          label-position="top"
        >
          <div style="display: flex; align-items: center; width: 100%">
            <BaseInput
              style="margin-right: 15px; flex: 1"
              type="text"
              placeholder="请输入短信验证码"
              clearable
              :maxlength="4"
              :inputTexts="['数字']"
              v-model="formData.verifyCode"
            />
            <BaseButton
              style="width: 100px"
              :disabled="disabled"
              :click="onSendSms"
            >
              {{ text }}
            </BaseButton>
          </div>
        </BaseFormItem>
      </BaseForm>
      <div style="margin-top: 35px; max-height: 150px; overflow-y: scroll">
        <div
          style="margin-bottom: 10px"
          v-for="(item, index) in withdrawalInfo.auditWidhdrawLog"
          :key="index"
        >
          <p>{{ item.authStatus === 1 ? '通过' : '驳回' }}：</p>
          <p>{{ item.remark }}</p>
          <p style="display: flex; justify-content: space-between">
            <span>{{ formatter.date(item.auditAt) }}</span>
            <span>{{ formatter.fen2yuan(item.price) }}</span>
          </p>
        </div>
      </div>
    </BaseTextContent>
    <template #footer>
      <BaseButton type="default" @click="remove">取消</BaseButton>
      <BaseButton type="primary" :click="onConfirm">提现</BaseButton>
    </template>
  </BaseDialog>
</template>

<script lang="tsx" name="account-main-withdrawal-dialog" setup>
import {
  defineEmits,
  withDefaults,
  defineProps,
  ref,
  onMounted,
  computed
} from 'vue';
import {
  BaseDialog,
  BaseButton,
  BaseInputNumber,
  BaseForm,
  BaseFormItem,
  XrkMessage,
  BaseInput,
  BaseTextContent,
  BaseNotification
} from 'xrk-components';
import {
  getEnterpriseAccountBalance,
  getEnterpriseBankInfo,
  sendWithdrawMsg,
  submitCashOut
} from '../data-source/apis';
import {
  GetEnterpriseAccountBalanceData,
  GetEnterpriseBankInfoData,
  SubmitCashOutParams
} from '../data-source/types';
import { PayPassagewayCode } from '@cTypes/bank';
import { translator, formatter } from '@/utils';
import { useCountdown } from '@/hooks';
import { FunctionCode } from '@/common/types/user';

const emits = defineEmits(['success']);
const props = withDefaults(
  defineProps<{
    remove?: () => {};
    payPassagewayCode: PayPassagewayCode;
    amount?: SubmitCashOutParams['amount']; // 提现金额，分，有值则不可输入
    idList?: SubmitCashOutParams['idList'];
    functionCode?: FunctionCode;
  }>(),
  {
    remove() {
      return () => {
        console.log('remove');
      };
    },
    functionCode: 2 // 默认服务商
  }
);

const { text, start, disabled, stop } = useCountdown();

const withdrawalInfo = ref<
  GetEnterpriseAccountBalanceData & GetEnterpriseBankInfoData
>({
  balance: 0,
  amount: 0,
  waitAmount: 0,
  companyName: '',
  bankName: '',
  openName: '',
  openBankNumber: '',
  auditWidhdrawLog: [],
  withdrawFee: 0,
  phone: ''
});
const formRef = ref(null);
const formData = ref({
  amount: props.amount ? translator.fen2yuan(props.amount) : 0, // 用户输入金额，元
  remark: '', // 备注
  verifyCode: '' // 验证码
});
const rules = {
  verifyCode: [{ required: true, message: '请输入', trigger: 'blur' }],
  amount: [
    {
      required: true,
      trigger: 'blur',
      validator: (_, value, callback) => {
        if (!value) {
          return callback(new Error('请输入'));
        }
        // 反向判断，优先err。
        if (
          !(formAmountFen.value >= minAmount.value) ||
          formAmountFen.value > withdrawalInfo.value.amount
        ) {
          return callback(
            new Error(
              `请输入${translator.fen2yuan(
                minAmount.value
              )}~${translator.fen2yuan(withdrawalInfo.value.amount)}之间的金额`
            )
          );
        }
        // 输入金额 >= 最小可提现金额 &&  输入金额 <= 可提现金额
        callback();
      }
    }
  ]
};

const isAspWithdrawal = computed(() => {
  return props.functionCode === 2;
});
const showAmountInput = computed(() => {
  return withdrawalInfo.value.amount > 0;
});
const formAmountFen = computed(() => {
  return translator.yuan2fen(formData.value.amount);
});
// 最小可提现金额，至少1毛钱
const minAmount = computed(() => withdrawalInfo.value.withdrawFee + 100);
// 实际到账金额
const amountReceived = computed(() => {
  return formAmountFen.value - withdrawalInfo.value.withdrawFee;
});
const amountDisabled = computed(() => {
  return !!props.amount;
});

const onSendSms = async () => {
  if (disabled.value) return;
  try {
    start();
    await sendWithdrawMsg();
    XrkMessage.success({
      message: '发送成功，请注意查收'
    });
  } catch (e) {
    stop();
  }
};
const onConfirm = async () => {
  await formRef.value.validate();
  try {
    await submitCashOut({
      payPassagewayCode: props.payPassagewayCode,
      ...formData.value,
      amount: formAmountFen.value,
      ...(props.idList && { idList: props.idList })
    });
    BaseNotification.success('提交成功');
  } catch (e) {
    BaseNotification({
      title: '提现失败',
      message: e.data.message,
      type: 'error'
    });
  }
  emits('success');
  props.remove();
};

onMounted(async () => {
  const [balance, bank] = await Promise.all([
    getEnterpriseAccountBalance(),
    getEnterpriseBankInfo()
  ]);
  Object.assign(withdrawalInfo.value, balance.data, bank.data);
});
</script>
<style lang="scss" scoped>
.form-dialog__w280 {
  width: 100%;
}
</style>
